import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { 
  FiX, FiInfo, FiImage, FiClock, FiMessageSquare, 
  FiPaperclip, FiSend, FiSave, FiEdit2, FiPhone, 
  FiMapPin, FiUser, FiLoader, FiBox, FiTrash2, 
  FiUpload, FiEye, FiAlertCircle
} from 'react-icons/fi';
import { Badge } from '../ui/Badge';
import supabase from '../../supabase/supabase';
import { useAuth } from '../../context/AuthContext';
import imageCompression from 'browser-image-compression';

// Helper to detect if a claim ID is a real database UUID or a mock ID
const isValidUUID = (id) => {
  if (!id) return false;
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id);
};

// Global session cache guarantees notes NEVER disappear from the UI
const sessionNotesCache = {};

const TabButton = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
      active 
        ? 'border-brand text-brand bg-brand/5' 
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
    }`}
  >
    <SafeIcon icon={icon} />
    {label}
  </button>
);

const DetailField = ({ label, value, icon, isEditing, onChange, type = "text" }) => (
  <div className="space-y-1.5">
    <label className="text-xs text-gray-400 font-semibold uppercase tracking-wider flex items-center gap-1.5">
      {icon && <SafeIcon icon={icon} size={12} />}
      {label}
    </label>
    {isEditing ? (
      type === "textarea" ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 resize-none min-h-[100px]"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand/20"
        />
      )
    ) : (
      <p className="text-sm font-medium text-gray-900 px-1">{value || 'Not provided'}</p>
    )}
  </div>
);

export const ClaimDrawer = ({ claim, isOpen, onClose, onUpdate }) => {
  const { profile } = useAuth();
  const isSuperAdmin = profile?.role === 'SUPER_ADMIN';
  
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Notes State
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [isNotesLoading, setIsNotesLoading] = useState(false);
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [noteContextMenu, setNoteContextMenu] = useState(null);
  const notesEndRef = useRef(null);

  // Assets State
  const [assets, setAssets] = useState([]);
  const [isAssetsLoading, setIsAssetsLoading] = useState(false);
  const [isUploadingAsset, setIsUploadingAsset] = useState(false);
  const [uploadStep, setUploadStep] = useState('');
  const [uploadErrorMsg, setUploadErrorMsg] = useState('');
  const assetInputRef = useRef(null);

  // Close context menu when clicking anywhere else
  useEffect(() => {
    const handleClickOutside = () => setNoteContextMenu(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  // Clear states when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setNoteContextMenu(null);
      setUploadErrorMsg('');
      setUploadStep('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (claim) {
      setFormData({
        client_name: claim.client_name || '',
        address: claim.address || '',
        phone_number: claim.phone_number || '',
        description: claim.description || claim.note || '',
        salvage_item: claim.salvage_item || claim.item || '',
      });
      setIsEditing(false);
      
      if (activeTab === 'notes') fetchNotes();
      if (activeTab === 'assets') fetchAssets();
    }
  }, [claim]);

  useEffect(() => {
    if (activeTab === 'notes' && claim?.id) {
      fetchNotes();
      notesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    if (activeTab === 'assets' && claim?.id) {
      fetchAssets();
    }
  }, [activeTab, claim?.id]);

  // --- NOTES LOGIC ---
  const fetchNotes = async (silent = false) => {
    if (!claim?.id || claim._isUnsaved) return;
    if (!silent) setIsNotesLoading(true);
    
    let dbNotes = [];
    if (isValidUUID(claim.id)) {
      try {
        const { data, error } = await supabase
          .from('claim_notes_1783454000000')
          .select('*')
          .eq('claim_id', claim.id)
          .order('created_at', { ascending: true });
        if (!error) dbNotes = data || [];
      } catch (err) {
        console.error('Error fetching notes:', err);
      }
    }

    const cached = sessionNotesCache[claim.id] || [];
    const merged = [...dbNotes];
    
    cached.forEach(cNote => {
      if (!merged.find(m => m.id === cNote.id || m.note_text === cNote.note_text)) {
        merged.push(cNote);
      }
    });

    merged.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    setNotes(merged);
    if (!silent) setIsNotesLoading(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !claim?.id || claim._isUnsaved) return;
    
    const noteContent = newNote.trim();
    const authorName = profile?.company_name || (isSuperAdmin ? 'Admin User' : 'Unknown Profile');
    const tempId = `temp-${Date.now()}`;
    const tempNote = {
      id: tempId,
      claim_id: claim.id,
      note_text: noteContent,
      author: authorName,
      created_at: new Date().toISOString()
    };

    if (!sessionNotesCache[claim.id]) sessionNotesCache[claim.id] = [];
    sessionNotesCache[claim.id].push(tempNote);
    
    setNotes(prev => [...prev, tempNote]);
    setNewNote('');

    if (!isValidUUID(claim.id)) return;

    setIsSubmittingNote(true);
    try {
      const { data, error } = await supabase
        .from('claim_notes_1783454000000')
        .insert([{
          claim_id: claim.id,
          note_text: noteContent,
          author: authorName
        }])
        .select();

      if (!error && data && data.length > 0) {
        sessionNotesCache[claim.id] = sessionNotesCache[claim.id].map(n => n.id === tempId ? data[0] : n);
        setNotes(prev => prev.map(n => n.id === tempId ? data[0] : n));
      }
    } catch (err) {
      console.error('Database Sync Error:', err);
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const handleNoteContextMenu = (e, note) => {
    e.preventDefault();
    setNoteContextMenu({ x: e.pageX, y: e.pageY, note });
  };

  const handleDeleteNote = async (noteToDelete) => {
    setNotes(prev => prev.filter(n => n.id !== noteToDelete.id));
    if (sessionNotesCache[claim.id]) {
      sessionNotesCache[claim.id] = sessionNotesCache[claim.id].filter(n => n.id !== noteToDelete.id);
    }
    setNoteContextMenu(null);

    if (noteToDelete.id && !String(noteToDelete.id).startsWith('temp-')) {
      try {
        const { error } = await supabase
          .from('claim_notes_1783454000000')
          .delete()
          .eq('id', noteToDelete.id);
        if (error) throw error;
      } catch (err) {
        console.error('Error deleting note from DB:', err);
      }
    }
  };

  // --- ASSETS LOGIC ---
  const fetchAssets = async () => {
    if (!isValidUUID(claim?.id)) return;
    setIsAssetsLoading(true);
    try {
      const { data, error } = await supabase
        .from('claim_assets_1783456000000')
        .select('*')
        .eq('claim_id', claim.id)
        .order('created_at', { ascending: false });
      
      if (!error) setAssets(data || []);
    } catch (err) {
      console.error('Error fetching assets:', err);
    } finally {
      setIsAssetsLoading(false);
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file || !claim?.id) return;

    if (claim._isUnsaved || !isValidUUID(claim.id)) {
      setUploadErrorMsg('Please save the claim to the database before uploading assets.');
      return;
    }

    setUploadErrorMsg('');
    setUploadStep('Starting upload...');
    setIsUploadingAsset(true);
    
    try {
      setUploadStep('Compressing image...');
      let fileToUpload = file;
      
      try {
        const options = {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1280,
          useWebWorker: false, 
        };
        fileToUpload = await imageCompression(file, options);
      } catch (compErr) {
        console.warn("Compression failed, using original file instead.", compErr);
        // Continue with original file if compression crashes
      }
      
      setUploadStep('Connecting to Storage Bucket...');
      const fileExt = fileToUpload.name.split('.').pop() || 'jpg';
      const fileName = `${claim.id}/${Date.now()}.${fileExt}`;
      let fileUrl = '';

      const { error: uploadError } = await supabase.storage
        .from('claim-assets')
        .upload(fileName, fileToUpload, { 
          upsert: true,
          contentType: fileToUpload.type || 'image/jpeg'
        });

      if (uploadError) {
        console.warn("Storage upload error, falling back to Base64 encoding:", uploadError);
        setUploadStep('Storage failed. Falling back to DB encoding...');
        
        // Fallback: Convert to Base64 and store directly in DB
        const reader = new FileReader();
        fileUrl = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error("Failed to read file into memory"));
          reader.readAsDataURL(fileToUpload);
        });
      } else {
        setUploadStep('Retrieving public URL...');
        const { data: publicUrlData } = supabase.storage
          .from('claim-assets')
          .getPublicUrl(fileName);
        fileUrl = publicUrlData.publicUrl;
      }

      setUploadStep('Saving record to Database...');
      const { data, error: dbError } = await supabase
        .from('claim_assets_1783456000000')
        .insert([{
          claim_id: claim.id,
          file_name: file.name,
          file_url: fileUrl
        }])
        .select();

      if (dbError) {
        throw new Error(`Database Insert Error: ${dbError.message}`);
      }
      
      if (data) {
        setAssets(prev => [data[0], ...prev]);
      }
      setUploadStep('');
    } catch (err) {
      console.error('Error uploading photo:', err);
      setUploadErrorMsg(err.message || 'An unknown error occurred during upload.');
    } finally {
      setIsUploadingAsset(false);
      setUploadStep('');
      if (assetInputRef.current) assetInputRef.current.value = '';
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm("Are you sure you want to delete this photo?")) return;
    
    // Optimistically update UI
    setAssets(prev => prev.filter(a => a.id !== assetId));
    
    try {
      const { error } = await supabase
        .from('claim_assets_1783456000000')
        .delete()
        .eq('id', assetId);
      
      if (error) throw error;
    } catch (err) {
      console.error("Error deleting asset:", err);
      fetchAssets(); // Refresh if failed
    }
  };

  // --- SAVE LOGIC ---
  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isValidUUID(claim.id)) {
        const { error } = await supabase
          .from('claims_1783447171616')
          .update(formData)
          .eq('id', claim.id);
        
        if (error) throw error;
      }
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating claim:', err);
      alert('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  if (!claim) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          />
          
          <motion.div 
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-xl font-bold text-gray-900">{claim.claim_number || claim.number}</h2>
                  {claim._isUnsaved ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700 uppercase tracking-wide">
                      Unsaved Draft
                    </span>
                  ) : (
                    <Badge status={claim.status}>{claim.status}</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {claim.salvage_item || claim.item || 'Unknown Item'} • {claim.make} {claim.model}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isSuperAdmin && activeTab === 'overview' && !claim._isUnsaved && (
                  isEditing ? (
                    <button 
                      onClick={handleSave}
                      disabled={isSaving}
                      className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-xl text-sm font-medium hover:bg-brand-light disabled:opacity-50 shadow-sm"
                    >
                      <SafeIcon icon={isSaving ? FiLoader : FiSave} className={isSaving ? 'animate-spin' : ''} />
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  ) : (
                    <button 
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 text-gray-700"
                    >
                      <SafeIcon icon={FiEdit2} />
                      Edit Details
                    </button>
                  )
                )}
                <button 
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
                >
                  <SafeIcon icon={FiX} size={24} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex px-6 border-b border-gray-100 bg-gray-50/50">
              <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} icon={FiInfo} label="More Details" />
              <TabButton active={activeTab === 'assets'} onClick={() => setActiveTab('assets')} icon={FiImage} label="Assets" />
              <TabButton active={activeTab === 'history'} onClick={() => setActiveTab('history')} icon={FiClock} label="Timeline" />
              <TabButton active={activeTab === 'notes'} onClick={() => setActiveTab('notes')} icon={FiMessageSquare} label="Notes" />
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8 space-y-8 bg-white">
              
              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && (
                <div className="space-y-10">
                  <section>
                    <h3 className="text-sm font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <div className="w-1.5 h-6 bg-brand rounded-full" />
                      Asset & BER Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4 p-5 bg-gray-50 rounded-2xl border border-gray-100">
                      <div className="col-span-2 md:col-span-1">
                        <DetailField 
                          label="Salvage Item Type" 
                          value={formData.salvage_item} 
                          icon={FiBox}
                          isEditing={isEditing}
                          onChange={(val) => setFormData({...formData, salvage_item: val})}
                        />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Make / Model</p>
                        <p className="font-medium text-gray-900">{claim.make || 'Unknown'} {claim.model || 'Unknown'}</p>
                      </div>
                      <div className="mt-2">
                        <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Serial / IMEI</p>
                        <p className="font-mono text-xs font-medium text-gray-700">{claim.serial_number || 'N/A'}</p>
                      </div>
                      <div className="mt-2">
                        <p className="text-xs text-gray-400 font-semibold uppercase mb-1">BER Reason</p>
                        <p className="text-red-600 font-bold">{claim.ber_reason || claim.note || 'Not specified'}</p>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h3 className="text-sm font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <div className="w-1.5 h-6 bg-brand rounded-full" />
                      Client Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <DetailField 
                        label="Client Name" 
                        value={formData.client_name} 
                        icon={FiUser}
                        isEditing={isEditing}
                        onChange={(val) => setFormData({...formData, client_name: val})}
                      />
                      <DetailField 
                        label="Phone Number" 
                        value={formData.phone_number} 
                        icon={FiPhone}
                        isEditing={isEditing}
                        onChange={(val) => setFormData({...formData, phone_number: val})}
                      />
                      <div className="md:col-span-2">
                        <DetailField 
                          label="Residential Address" 
                          value={formData.address} 
                          icon={FiMapPin}
                          isEditing={isEditing}
                          type="textarea"
                          onChange={(val) => setFormData({...formData, address: val})}
                        />
                      </div>
                    </div>
                  </section>

                  <section>
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <div className="w-1.5 h-6 bg-brand rounded-full" />
                      Claim Description
                    </h3>
                    {isEditing ? (
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 resize-none min-h-[120px]"
                        placeholder="Add technical findings or incident description..."
                      />
                    ) : (
                      <p className="text-sm text-gray-600 leading-relaxed bg-white p-4 rounded-2xl border border-dashed border-gray-200">
                        {formData.description || 'No detailed technical description provided for this claim.'}
                      </p>
                    )}
                  </section>
                </div>
              )}

              {/* ASSETS TAB */}
              {activeTab === 'assets' && (
                <div className="space-y-6">
                  {/* Upload Header */}
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-between items-center bg-gray-50 p-4 rounded-2xl border border-gray-100">
                      <div>
                        <h3 className="font-bold text-gray-900">Claim Assets & Media</h3>
                        <p className="text-xs text-gray-500">Upload and manage photos of the device damage.</p>
                      </div>
                      
                      <input 
                        type="file" 
                        accept="image/*"
                        ref={assetInputRef}
                        onChange={handleUploadPhoto}
                        className="hidden"
                      />

                      <button 
                        onClick={() => {
                          if (claim._isUnsaved || !isValidUUID(claim.id)) {
                            setUploadErrorMsg('Please save the claim to the database before uploading photos.');
                          } else {
                            assetInputRef.current?.click();
                          }
                        }}
                        disabled={isUploadingAsset || claim._isUnsaved || !isValidUUID(claim.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-xl text-sm font-bold hover:bg-brand-light transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploadingAsset ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiUpload} />}
                        {isUploadingAsset ? 'Uploading...' : 'Upload Photo'}
                      </button>
                    </div>

                    {/* Live Feedback Messages */}
                    {isUploadingAsset && uploadStep && (
                      <div className="text-xs text-brand font-medium flex items-center gap-2 px-2 animate-pulse">
                        <SafeIcon icon={FiLoader} className="animate-spin" /> {uploadStep}
                      </div>
                    )}
                    {uploadErrorMsg && (
                      <div className="bg-red-50 text-red-600 p-3 rounded-xl border border-red-100 text-sm flex items-start gap-2 animate-in fade-in">
                        <SafeIcon icon={FiAlertCircle} className="flex-shrink-0 mt-0.5" />
                        <div className="flex-1 font-medium">{uploadErrorMsg}</div>
                      </div>
                    )}
                  </div>

                  {/* Assets Grid */}
                  {isAssetsLoading ? (
                    <div className="flex justify-center py-12 text-gray-400">
                      <SafeIcon icon={FiLoader} className="animate-spin text-3xl" />
                    </div>
                  ) : assets.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[300px] text-center bg-gray-50/50 rounded-3xl border border-dashed border-gray-200 p-6">
                      <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-4 text-gray-300 shadow-sm border border-gray-100">
                        <SafeIcon icon={FiImage} size={28} />
                      </div>
                      <h3 className="text-gray-900 font-bold text-base mb-1">No Media Uploaded</h3>
                      <p className="text-sm text-gray-500 max-w-xs">High-resolution photos will be securely stored and automatically compressed to save space.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {assets.map(asset => (
                        <div key={asset.id} className="group relative aspect-square bg-gray-100 rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                          <img src={asset.file_url} alt={asset.file_name} className="w-full h-full object-cover" />
                          
                          {/* Hover Actions */}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3">
                            <a 
                              href={asset.file_url} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="p-2 bg-white/20 hover:bg-brand rounded-full text-white backdrop-blur-sm transition-colors"
                              title="View Full Size"
                            >
                              <SafeIcon icon={FiEye} size={18} />
                            </a>
                            <button 
                              onClick={() => handleDeleteAsset(asset.id)}
                              className="p-2 bg-white/20 hover:bg-red-500 rounded-full text-white backdrop-blur-sm transition-colors"
                              title="Delete Photo"
                            >
                              <SafeIcon icon={FiTrash2} size={18} />
                            </button>
                          </div>
                          
                          {/* Label */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8 pb-3 pointer-events-none">
                            <p className="text-white text-[10px] font-medium truncate">{asset.file_name}</p>
                            <p className="text-white/60 text-[9px] mt-0.5">
                              {new Date(asset.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* TIMELINE TAB */}
              {activeTab === 'history' && (
                <div className="relative pl-8 space-y-10 before:content-[''] before:absolute before:left-[15px] before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100">
                  <div className="relative">
                    <div className="absolute -left-[27px] top-1 w-7 h-7 rounded-full bg-white border-2 border-brand flex items-center justify-center z-10 shadow-sm">
                      <SafeIcon icon={FiClock} size={12} className="text-brand" />
                    </div>
                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                      <p className="font-bold text-gray-900 text-sm">Claim Initialized</p>
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                        <SafeIcon icon={FiUser} size={10} />
                        System Automator • {new Date(claim.created_at || claim.allocatedAt || new Date()).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-600 mt-2 italic">Data synchronized from database.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* NOTES TAB */}
              {activeTab === 'notes' && (
                <div className="flex flex-col h-full min-h-[400px]">
                  {claim._isUnsaved ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 italic text-sm space-y-3">
                      <SafeIcon icon={FiMessageSquare} size={32} className="text-gray-200" />
                      <p>Please save this claim to the database before adding notes.</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 overflow-y-auto space-y-4 mb-6 pr-2">
                        {isNotesLoading && notes.length === 0 ? (
                          <div className="flex justify-center items-center h-32 text-gray-400">
                            <SafeIcon icon={FiLoader} className="animate-spin text-2xl" />
                          </div>
                        ) : notes.length === 0 ? (
                          <div className="flex flex-col items-center justify-center text-gray-400 italic text-sm h-full pt-10">
                            <SafeIcon icon={FiMessageSquare} size={32} className="text-gray-200 mb-2" />
                            <p>No collaboration notes yet.</p>
                          </div>
                        ) : (
                          <>
                            {notes.map(note => (
                              <div 
                                key={note.id} 
                                className={`bg-gray-50 p-4 rounded-2xl border border-gray-100 animate-in fade-in slide-in-from-bottom-2 duration-300 transition-colors cursor-context-menu ${
                                  noteContextMenu?.note?.id === note.id ? 'ring-2 ring-brand/30 bg-brand/5' : 'hover:bg-gray-100/50'
                                }`}
                                onContextMenu={(e) => handleNoteContextMenu(e, note)}
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <span className="font-semibold text-sm text-gray-900">{note.author}</span>
                                  <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">
                                    {new Date(note.created_at).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{note.note_text}</p>
                              </div>
                            ))}
                            <div ref={notesEndRef} />
                          </>
                        )}
                      </div>

                      <div className="bg-white border border-gray-200 p-2 rounded-3xl shadow-sm mt-auto relative focus-within:ring-2 focus-within:ring-brand/20 transition-all">
                        <textarea
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleAddNote();
                            }
                          }}
                          placeholder="Share an update or internal note..."
                          className="w-full p-4 bg-transparent border-none rounded-2xl focus:outline-none text-sm resize-none"
                          rows={2}
                        />
                        <div className="flex justify-between items-center px-2 pb-2">
                          <button className="flex items-center gap-2 text-gray-400 hover:text-brand transition-colors text-xs font-medium">
                            <SafeIcon icon={FiPaperclip} />
                            Attach Reference
                          </button>
                          <button 
                            onClick={handleAddNote}
                            disabled={isSubmittingNote || !newNote.trim()}
                            className="p-2.5 bg-brand text-white rounded-xl shadow-md hover:bg-brand-light transition-all disabled:opacity-50 disabled:hover:bg-brand"
                          >
                            {isSubmittingNote ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSend} />}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          {/* Note Context Menu */}
          {noteContextMenu && (
            <div 
              className="fixed bg-white shadow-xl rounded-xl border border-gray-100 py-2 w-48 z-[100] animate-in fade-in zoom-in duration-100"
              style={{ top: noteContextMenu.y, left: noteContextMenu.x }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={() => handleDeleteNote(noteContextMenu.note)}
                className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 font-medium transition-colors"
              >
                <SafeIcon icon={FiTrash2} />
                Delete Note
              </button>
            </div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};